import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Observable, Subscription, timer, of, race} from 'rxjs';
import {filter, map, take} from 'rxjs/operators';
import {ProjectId} from '@webmate-web-core/shared/types/ids';
import {WorkbenchIpcService} from './workbench-ipc.service';
import {
    WORKBENCH_IPC_HOST_QUERY_TIMEOUT_MS,
    WORKBENCH_IPC_REGISTRY_FRESHNESS_MS,
    WorkbenchIpcMessage,
    WorkbenchRegistryEntry,
    isWorkbenchPresenceMessage,
    workbenchRegistryEntryFromMessage
} from './workbench-ipc.types';

export interface WorkbenchHostQueryOptions {
    /** Ignore cached registry entries and accept only a live claim as proof of a host. */
    ignoreCachedEntries?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class WorkbenchIpcRegistryService implements OnDestroy {
    private readonly entriesByProject = new Map<ProjectId, Map<string, WorkbenchRegistryEntry>>();
    private readonly entriesSubject = new BehaviorSubject<WorkbenchRegistryEntry[]>([]);
    private readonly subscriptions = new Subscription();

    constructor(private readonly workbenchIpcService: WorkbenchIpcService) {
        this.subscriptions.add(this.workbenchIpcService.observe().subscribe(message => this.handleMessage(message)));
    }

    observeEntries(): Observable<WorkbenchRegistryEntry[]> {
        return this.entriesSubject.asObservable();
    }

    getEntries(projectId?: ProjectId): WorkbenchRegistryEntry[] {
        this.pruneStaleEntries();
        const entries = Array.from(this.entriesByProject.values()).flatMap(projectEntries => Array.from(projectEntries.values()));
        return projectId ? entries.filter(entry => entry.projectId === projectId) : entries;
    }

    getFreshHost(projectId: ProjectId): WorkbenchRegistryEntry | undefined {
        const candidates = this.getEntries(projectId).filter(entry => entry.ready && this.isFresh(entry));
        return this.sortHosts(candidates)[0];
    }

    /**
     * Locates the hosting workbench for a project.
     *
     * With `ignoreCachedEntries` the cached registry is bypassed entirely and only a live
     * `workbench:claim` counts as a host. Callers use this when they have independent evidence that
     * a known host has died (e.g. the window handle they opened is closed) but its entry can still
     * be inside the freshness window.
     */
    queryHost(projectId: ProjectId,
              timeoutMs = WORKBENCH_IPC_HOST_QUERY_TIMEOUT_MS,
              options: WorkbenchHostQueryOptions = {}): Observable<WorkbenchRegistryEntry | undefined> {
        if (!options.ignoreCachedEntries) {
            const freshHost = this.getFreshHost(projectId);
            if (freshHost) {
                return of(freshHost);
            }
        }

        const query = this.workbenchIpcService.query(projectId);
        const claim$ = this.workbenchIpcService.observeType('workbench:claim').pipe(
            filter(message => message.senderRole === 'workbench' && message.projectId === projectId && message.correlationId === query.correlationId),
            map(message => workbenchRegistryEntryFromMessage(message)),
            take(1)
        );
        const timeout$ = timer(timeoutMs).pipe(
            map(() => options.ignoreCachedEntries ? undefined : this.getFreshHost(projectId))
        );
        return race(claim$, timeout$);
    }

    removeEntry(projectId: ProjectId, senderId: string): void {
        const projectEntries = this.entriesByProject.get(projectId);
        projectEntries?.delete(senderId);
        this.emitEntries();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.entriesSubject.complete();
    }

    private handleMessage(message: WorkbenchIpcMessage): void {
        if (!message.projectId || message.senderId === this.workbenchIpcService.senderId) {
            return;
        }

        switch (message.type) {
            case 'workbench:announce':
            case 'workbench:heartbeat':
            case 'workbench:claim':
                if (isWorkbenchPresenceMessage(message)) {
                    this.upsertEntry(workbenchRegistryEntryFromMessage(message));
                }
                return;
            case 'workbench:query':
                this.pruneStaleEntries();
                return;
        }
    }

    private upsertEntry(entry: WorkbenchRegistryEntry): void {
        let projectEntries = this.entriesByProject.get(entry.projectId);
        if (!projectEntries) {
            projectEntries = new Map<string, WorkbenchRegistryEntry>();
            this.entriesByProject.set(entry.projectId, projectEntries);
        }
        projectEntries.set(entry.senderId, entry);
        this.emitEntries();
    }

    private pruneStaleEntries(): void {
        let changed = false;
        this.entriesByProject.forEach((projectEntries, projectId) => {
            Array.from(projectEntries.values()).forEach(entry => {
                if (!this.isFresh(entry)) {
                    projectEntries.delete(entry.senderId);
                    changed = true;
                }
            });
            if (projectEntries.size === 0) {
                this.entriesByProject.delete(projectId);
            }
        });
        if (changed) {
            this.emitEntries();
        }
    }

    private isFresh(entry: WorkbenchRegistryEntry): boolean {
        return Date.now() - entry.lastSeenAt <= WORKBENCH_IPC_REGISTRY_FRESHNESS_MS;
    }

    private sortHosts(entries: WorkbenchRegistryEntry[]): WorkbenchRegistryEntry[] {
        return entries.sort((a, b) => a.createdAt - b.createdAt || a.senderId.localeCompare(b.senderId));
    }

    private emitEntries(): void {
        this.entriesSubject.next(Array.from(this.entriesByProject.values()).flatMap(projectEntries => Array.from(projectEntries.values())));
    }
}
